import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { categories } from '@/mocks/movies';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-movie-2-line text-background-50 text-lg"></i>
            </div>
            <span className="text-xl font-heading text-foreground-950">
              番茄影视
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/') && location.pathname === '/'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              首页
            </Link>
            {categories.filter((c) => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isActive(`/category/${cat.slug}`)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                {cat.label}
              </Link>
            ))}
            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/about')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {searchOpen ? (
              <div className="flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2 border border-background-200">
                <i className="ri-search-line text-foreground-500 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索影视..."
                  className="bg-transparent text-sm text-foreground-900 outline-none w-40 placeholder:text-foreground-400"
                  autoFocus
                />
                <button
                  onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                  className="w-5 h-5 flex items-center justify-center cursor-pointer text-foreground-400 hover:text-foreground-700"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-search-line text-foreground-600 text-lg"></i>
              </button>
            )}
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className={`text-foreground-700 text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              首页
            </Link>
            {categories.filter((c) => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                {cat.label}
              </Link>
            ))}
            <Link
              to="/about"
              className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              关于我们
            </Link>
            <div className="mt-2 px-4">
              <div className="flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2 border border-background-200">
                <i className="ri-search-line text-foreground-500 text-sm"></i>
                <input
                  type="text"
                  placeholder="搜索影视..."
                  className="bg-transparent text-sm text-foreground-900 outline-none w-full placeholder:text-foreground-400"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}